import { useEffect, useState } from "react"
import { createPlan, deletePlan, getPlans, updatePlan } from "../../api/api"

const emptyPlan = {
  name: "",
  price: 0,
  limits: {
    projects: 1,
    pagesCrawl: 25,
    keywordsTracked: 50,
    backlinks: 100,
    apiRequests: 1000
  },
  features: "SEO audit\nKeyword research\nBacklink analytics"
}

const LIMITS = [
  ["projects", "Projects"],
  ["pagesCrawl", "Pages per audit"],
  ["keywordsTracked", "Tracked keywords"],
  ["backlinks", "Backlink rows"],
  ["apiRequests", "API requests"]
]

export default function Plans() {
  const [plans, setPlans] = useState([])
  const [form, setForm] = useState(emptyPlan)
  const [editingId, setEditingId] = useState("")
  const [status, setStatus] = useState("")

  async function load() {
    const res = await getPlans()
    setPlans(res.data || [])
  }

  useEffect(() => {
    load().catch((err) => setStatus(err.response?.data?.error || "Failed to load plans"))
  }, [])

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  function updateLimit(field, value) {
    setForm((current) => ({
      ...current,
      limits: {
        ...current.limits,
        [field]: Number(value)
      }
    }))
  }

  function edit(plan) {
    setEditingId(plan._id)
    setForm({
      name: plan.name || "",
      price: plan.price || 0,
      limits: {
        projects: plan.limits?.projects || 0,
        pagesCrawl: plan.limits?.pagesCrawl || 0,
        keywordsTracked: plan.limits?.keywordsTracked || 0,
        backlinks: plan.limits?.backlinks || 0,
        apiRequests: plan.limits?.apiRequests || 0
      },
      features: (plan.features || []).join("\n")
    })
  }

  async function save(e) {
    e.preventDefault()
    setStatus("Saving plan...")

    const payload = {
      ...form,
      price: Number(form.price || 0),
      features: String(form.features || "")
        .split(/\n|,/)
        .map((item) => item.trim())
        .filter(Boolean)
    }

    try {
      if (editingId) {
        await updatePlan(editingId, payload)
      } else {
        await createPlan(payload)
      }
      setForm(emptyPlan)
      setEditingId("")
      await load()
      setStatus("Plan saved.")
    } catch (err) {
      setStatus(err.response?.data?.error || "Could not save plan")
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this plan?")) return
    await deletePlan(id)
    await load()
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Plans & Limits</h1>
          <p className="muted">Build paid packages and control projects, crawl pages, keywords, backlinks and API usage.</p>
        </div>
      </div>

      <div className="split-grid">
        <form className="card" onSubmit={save}>
          <div className="settings-panel-head">
            <div>
              <p className="eyebrow">{editingId ? "Edit Plan" : "New Plan"}</p>
              <h2>{editingId ? "Update limits" : "Create package"}</h2>
            </div>
            <button>{editingId ? "Update plan" : "Create plan"}</button>
          </div>

          <div className="settings-grid">
            <label>
              Plan name
              <input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Starter" required />
            </label>
            <label>
              Monthly price
              <input type="number" value={form.price} onChange={(e) => update("price", e.target.value)} />
            </label>
            {LIMITS.map(([key, label]) => (
              <label key={key}>
                {label}
                <input type="number" value={form.limits[key]} onChange={(e) => updateLimit(key, e.target.value)} />
              </label>
            ))}
            <label>
              Features
              <textarea value={form.features} onChange={(e) => update("features", e.target.value)} />
            </label>
          </div>

          {editingId && (
            <button type="button" className="secondary" onClick={() => { setEditingId(""); setForm(emptyPlan) }}>
              Cancel edit
            </button>
          )}
          {status && <p className="muted">{status}</p>}
        </form>

        <div className="card">
          <h2>How Limits Work</h2>
          <div className="insight-list">
            <div className="insight-item">
              <strong>Projects</strong>
              <p className="muted">How many client/domain projects a plan can manage.</p>
            </div>
            <div className="insight-item">
              <strong>Pages per audit</strong>
              <p className="muted">Controls crawl size and server load.</p>
            </div>
            <div className="insight-item">
              <strong>Keywords and API requests</strong>
              <p className="muted">Use these for higher tiers when SERP/API integrations are enabled.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid pro-section">
        {plans.map((plan) => (
          <div className="card plan-card" key={plan._id}>
            <div className="score-bar-top">
              <div>
                <p className="eyebrow">Plan</p>
                <h2>{plan.name}</h2>
              </div>
              <div className="metric">{plan.price || 0}</div>
            </div>
            <div className="limit-grid">
              {LIMITS.map(([key, label]) => (
                <div key={key}>
                  <span className="muted">{label}</span>
                  <strong>{plan.limits?.[key] || 0}</strong>
                </div>
              ))}
            </div>
            <div className="feature-list">
              {(plan.features || []).map((feature) => (
                <span className="pill low" key={feature}>{feature}</span>
              ))}
            </div>
            <div className="actions">
              <button type="button" onClick={() => edit(plan)}>Edit</button>
              <button type="button" className="danger" onClick={() => remove(plan._id)}>Delete</button>
            </div>
          </div>
        ))}
        {plans.length === 0 && <div className="empty-state">No plans yet. Create Starter, Pro and Agency plans.</div>}
      </div>
    </div>
  )
}
